import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import SEO, { SEO_DATA } from "@/components/SEO";

const ContactPage = () => {
  const { t } = useTranslation();
  
  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.address'),
      content: "Av. Reforma 9-00, Zona 9",
      subcontent: "Guatemala, Guatemala"
    },
    {
      icon: Phone,
      title: t('contact.info.phone'),
      content: "+502 4254-7204",
      link: "tel:+50242547204"
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      content: "hola@ikonicdeals.com",
      link: "mailto:hola@ikonicdeals.com"
    },
    {
      icon: Clock,
      title: t('contact.info.hours'),
      content: t('contact.info.weekdays'),
      subcontent: "9:00 AM - 6:00 PM"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      <SEO 
        title={SEO_DATA.contact.title}
        description={SEO_DATA.contact.description}
        keywords={SEO_DATA.contact.keywords}
        url="/contacto"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              {t('contact.label')}
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl text-white leading-tight">
              {t('contact.heroTitle')}
              <span className="text-[#D4AF37]"> {t('contact.heroHighlight')}</span>
            </h1>
            <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed">
              {t('contact.heroDescription')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="pb-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="p-6 bg-[#121212] border border-white/5"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 mb-4">
                      <info.icon size={22} className="text-[#D4AF37]" />
                    </div>
                    <h3 className="text-white font-medium mb-2">{info.title}</h3>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-[#A3A3A3]">{info.content}</p>
                    )}
                    {info.subcontent && (
                      <p className="text-[#A3A3A3] text-sm mt-1">{info.subcontent}</p>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Social Links */}
              <div className="bg-[#121212] border border-white/5 p-8">
                <h3 className="text-white font-medium mb-6">{t('contact.followUs')}</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com/ikonicdealsgt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-3 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all"
                    data-testid="social-instagram-link"
                  >
                    <Instagram size={20} />
                    @ikonicdealsgt
                  </a>
                  <a 
                    href="https://facebook.com/IkonicDealsGuatemala"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-3 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all"
                    data-testid="social-facebook-link"
                  >
                    <Facebook size={20} />
                    Ikonic Deals
                  </a>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-8 p-8 bg-[#D4AF37]/5 border border-[#D4AF37]/20"
              >
                <h3 className="text-white text-lg mb-3">{t('contact.whatsappTitle')}</h3>
                <p className="text-[#A3A3A3] mb-4">
                  {t('contact.whatsappDescription')}
                </p>
                <a 
                  href={`https://wa.me/50242547204?text=${encodeURIComponent("¡Hola Ikonic Deals! 👋\n\nMe gustaría recibir información sobre sus servicios inmobiliarios.\n\n¿Podrían ayudarme?")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#FFE082] transition-colors"
                  data-testid="whatsapp-cta"
                >
                  <Phone size={18} />
                  {t('contact.openWhatsapp')}
                </a>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#121212] border border-white/5 p-8 md:p-10"
            >
              <h2 className="text-2xl text-white mb-2">{t('contact.formTitle')}</h2>
              <p className="text-[#A3A3A3] mb-8">
                {t('contact.formSubtitle')}
              </p>
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
